import { observable, action, runInAction, computed, toJS, makeObservable } from 'mobx';

import LoglyticsConnection, { ILoglyticsUpdate } from '../services/connection/loglytics.connection';
import { Severity, SeverityExc } from '../models/log.model';
import { ILoglytics, LoglyticsStatus } from '../models/loglytics.model';
import { StatDeviceNumber, StatNumber, StatDateNumbers, StatUnit, statUnit } from '../models/stat.model';
import { RangeMode, SearchParam } from '../models/search.model';
import { rangeModeToFromTime, rangeModeToInterval } from '../components/common/time_range_buttons.component';
import statsConnection from '../services/connection/stats.connection';
import loglyticsConnection from '../services/connection/loglytics.connection';
// import searchStore from './search.store';
// import logsStore from './logs.store';
import CommonStore from './common.store';
import searchStore from './search.store';
import logsStore from './logs.store';
import { getLogger } from '../services/logger.service';
import memberStore from './member.store';

const log = getLogger('LoglyticsStore');

class SearchParams {
  severity: Severity = Severity.ERROR;
}

export class LoglyticsStore {
  _loglytics: ILoglytics[] = [];
  version: string = '';
  appVersions: string[] = [];
  deviceCount?: StatDeviceNumber = {device: 0, error: 0, warning: 0, exception: 0};
  average: StatNumber = {exception: 0, error: 0, warning: 0};
  affectedDevices: StatDateNumbers[] = [];
  severity: SeverityExc = Severity.ERROR;
  startDate?: Date = undefined;
  endDate?: Date = undefined;
  currentLoglytics?: ILoglytics = undefined;
  currentSampleIndex = -1;
  loading = false;
  analysisLoading = false;
  analysisError?: string = undefined;
  prLoading = false;

  unreadCount: {exception: number, error: number, warning: number} = {exception:0, error: 0, warning: 0};

  loglyticsStatus: LoglyticsStatus | '' = LoglyticsStatus.OPEN;

  // search params
  searchParams: SearchParams = new SearchParams();

  range: RangeMode = RangeMode.DAY;

  clear() {
    this._loglytics = [];
  }

  get loglytics(): (ILoglytics & {read?: boolean})[] {
    const loglytics:  (ILoglytics & {read?
      : boolean})[] = toJS(this._loglytics);
    // const memberId = memberStore.member!._id;
    // loglytics.forEach(l =>  l.read = l.membersRead && !!l.membersRead.find(id => id === memberId));
    return loglytics;
  }

  // @computed get sampleList() : ISample[] {
  //   if (!this.currentLoglytics) return [];
  //   let list: {session: string, log: string, _id:string}[] = [];
  //   this.currentLoglytics.daily.forEach(day => 
  //     day.sampleList?.forEach(sample => list.unshift(sample))
  //   );
  //   return list;
  // }

  // @computed get currentSample() {
  //   return this.sampleList[this.currentSampleIndex];
  // }

  get unreadSum() {
    // return (memberStore!.app!.platform === Platform.ANDROID ? this.unreadCount.exception : 0) + this.unreadCount.error + this.unreadCount.warning;
    return 0;
  }

  get unread() {
    let count = 0;
    this.loglytics?.forEach(l => {if (!l.read) ++count;});
    return count;
  }

  get currentDateHistogram() {
    if (!this.currentLoglytics) return [];

    const dateHistogram = this.currentLoglytics.dateHistogram!.map(bucket => {
      return {...bucket, percentage: bucket.total ? Math.min(bucket.affected / bucket.total, 1) : 0}
    });
    return dateHistogram;
  }

  constructor () {
    makeObservable(this, {
      _loglytics: observable,
      version: observable,
      appVersions: observable,
      deviceCount: observable,
      average: observable,
      affectedDevices: observable,
      severity: observable,
      startDate: observable,
      endDate: observable,
      currentLoglytics: observable,
      currentSampleIndex: observable,
      loading: observable,
      analysisLoading: observable,
      analysisError: observable,
      prLoading: observable,
      runAnalysis: action,
      createFixPr: action,
      unreadCount: observable,
      loglyticsStatus: observable,
      searchParams: observable,
      range: observable,
      clear: action,
      loglytics: computed,
      unreadSum: computed,
      unread: computed,
      currentDateHistogram: computed,
      setVersion: action,
      updateIssue: action,
      setLoglyticsStatus: action,
      setSeverity: action,
      getLoglytics: action,
      setKey: action,
      clearCurrent: action,
      setCurrentLoglytics: action,
      nextPrevSample: action,
      setLoglyticsRead: action,
      setAllRead: action,
      getUnreadCount: action,
      setLoading: action,
      setRange: action
    });

    this.getLoglytics = this.getLoglytics.bind(this);
    this.setSeverity = this.setSeverity.bind(this);
    this.nextPrevSample = this.nextPrevSample.bind(this);
  }

  // tslint:disable-next-line:no-shadowed-variable
  setVersion(version: string) {
    this.version = version;
    this.getLoglytics();
  }

  async updateIssue(key: string, update: ILoglyticsUpdate) {
    const index = this._loglytics.findIndex(l => l.key === key);
    const previousRow = index >= 0 ? {...this._loglytics[index]} : undefined;
    const previousCurrent = this.currentLoglytics?.key === key ? {...this.currentLoglytics} : undefined;

    const apply = (issue: ILoglytics) => {
      if (update.status !== undefined) issue.status = update.status;
    };
    if (index >= 0) apply(this._loglytics[index]);
    if (previousCurrent) apply(this.currentLoglytics!);

    try {
      const updated = await LoglyticsConnection.updateLoglytics(key, update);
      // The server snapshots resolvedOnVersion when marking fixed — reflect it without a refetch
      runInAction(() => {
        if (index >= 0) this._loglytics[index].resolvedOnVersion = updated.resolvedOnVersion;
        if (this.currentLoglytics?.key === key) this.currentLoglytics.resolvedOnVersion = updated.resolvedOnVersion;
      });
    } catch (error) {
      log.e('error updating issue', error);
      runInAction(() => {
        if (previousRow) this._loglytics[index] = previousRow;
        if (previousCurrent) this.currentLoglytics = previousCurrent;
      });
    }
  }

  async setLoglyticsStatus(status: LoglyticsStatus | '') {
    this.loglyticsStatus = status;
    await this.getLoglytics();
  }

  setSeverity(severity: SeverityExc) {
    this.severity = severity;
  }

  async getLoglytics(all: boolean = false) {
    this.setLoading(true);
    try {
      const unit = statUnit(memberStore.app?.platform);
      const data: { appVersion?: string; startDate: string; timeInterval: string; statUnit: StatUnit } = {
        startDate: rangeModeToFromTime(this.range).toISOString(),
        timeInterval: rangeModeToInterval(this.range, memberStore.app?.platform),
        statUnit: unit,
      };
      if (this.version.length > 0) data.appVersion = this.version;

      const loglyticsData: { severity: SeverityExc; fromTime: string; statUnit: StatUnit; appVersion?: string; status?: LoglyticsStatus | '' } = {
        severity: this.severity,
        fromTime: rangeModeToFromTime(this.range).toISOString(),
        statUnit: unit,
      };
      // Always sent explicitly: the server defaults a missing status to open, '' means all
      loglyticsData.status = all ? '' : this.loglyticsStatus;
      if (!all && this.version.length > 0) loglyticsData.appVersion = this.version;

      // The graph stats follow the same status scope as the list (not appVersions — the dropdown stays complete)
      const statData = {...data, status: loglyticsData.status};
      const [loglytics, appVersions, deviceCount, average, affectedDevices] = await Promise.all([
        LoglyticsConnection.get(loglyticsData),
        statsConnection.getAppVersions(data),
        statsConnection.getDeviceCount(statData),
        statsConnection.getAverages(statData),
        statsConnection.getAffectedDevices(statData),
      ]);

      log.d('loglytics received', loglytics);
      runInAction(() => {
        this.appVersions = appVersions
        this.deviceCount = deviceCount;
        this._loglytics = loglytics;
        this.average = average;
        this.affectedDevices = affectedDevices;
      });
    } catch (error) {
      log.e('error getting loglytics', error);
    } finally {
      this.setLoading(false);
    }
  }

  async setKey(key: string, force = false) {
    if (!force && this.currentLoglytics?.key === key) return;
    try {
      const loglytics = await loglyticsConnection.getOne(key, {
        startDate: rangeModeToFromTime(this.range).toISOString(),
        timeInterval: rangeModeToInterval(this.range, memberStore.app?.platform),
        statUnit: statUnit(memberStore.app?.platform),
      });
      log.d('loglytics one received', loglytics);
      runInAction(() => {
        this.currentLoglytics = loglytics;
      });
    } catch (error) {
      log.e('error setting key', error);
    }
  }

  clearCurrent() {
    this.currentLoglytics = undefined;
  }

  async createFixPr(key: string) {
    this.prLoading = true;
    this.analysisError = undefined;
    try {
      const {prUrl} = await LoglyticsConnection.createFixPr(key);
      runInAction(() => {
        if (this.currentLoglytics?.analysis) this.currentLoglytics.analysis.prUrl = prUrl;
      });
    } catch (error) {
      log.e('error creating fix PR', error);
      runInAction(() => {
        this.analysisError = error.message || 'Creating the pull request failed. Try again.';
      });
    } finally {
      runInAction(() => {
        this.prLoading = false;
      });
    }
  }

  async runAnalysis(key: string, force = false) {
    this.analysisLoading = true;
    this.analysisError = undefined;
    try {
      const analysis = await LoglyticsConnection.runAnalysis(key, force);
      runInAction(() => {
        if (this.currentLoglytics) this.currentLoglytics.analysis = analysis ?? undefined;
        if (!analysis) this.analysisError = 'The analysis did not produce a result. Try again.';
      });
    } catch (error) {
      log.e('error running analysis', error);
      runInAction(() => {
        this.analysisError = error.message || 'The analysis failed. Try again.';
      });
    } finally {
      runInAction(() => {
        this.analysisLoading = false;
      });
    }
  }

  async setCurrentLoglytics(key: string, sessionId?: string, logId?: string) {
    await this.setKey(key);
    if (!this.currentLoglytics) return;
    if (!sessionId || !logId) {
      searchStore.setLoglyticsKey(this.currentLoglytics.key, this.range);
    }
    else searchStore.setSessionLog(sessionId, logId);
    await logsStore.getLogs(true);
  }

  async nextPrevSample(_next: boolean) {
    // if (next) ++this.currentSampleIndex;
    // else --this.currentSampleIndex;
    // let sample = this.currentSample;
    // if (!sample) {
    //   console.error('no sample for current index ' + this.currentSampleIndex);
    //   return;
    // }
    // searchStore.setSessionLog(sample.session, sample.log);
    // await logsStore.getLogs(true);
  }

  async setLoglyticsRead(_read: boolean, _loglyticsId?: string) {
    // if (!loglyticsId) loglyticsId = this.currentLoglytics?._id;
    // if (!loglyticsId) return;
    // try {
    //   let loglyticsList: ILoglytics[];
    //   if (read) loglyticsList = await loglyticsConnection.read([loglyticsId!]);
    //   else loglyticsList = await loglyticsConnection.unread([loglyticsId!]);
    //   this.getUnreadCount();
    //   console.log(loglyticsList);
    //   runInAction(() => {
    //     if (this.currentLoglytics) {
    //       this.currentLoglytics.membersRead = loglyticsList[0].membersRead;
    //     }
    //     let loglytic = this._loglytics.find(l => l._id === loglyticsId);    
    //     if (loglytic) {
    //       loglytic.membersRead = loglyticsList[0].membersRead;
    //     }
    //   });
    // } catch (error) {
    //   console.log(error);
    // } 
  }

  async setAllRead() {
    // const ids = this.loglytics.filter(l => !l.read).map(l => l._id);
    // if (ids.length === 0) return;
    // try {
    //   let loglytics = await loglyticsConnection.read(ids);
    //   await this.getUnreadCount();
    //   runInAction(() => {
    //     loglytics.forEach((l) => {
    //       let index = this._loglytics.findIndex((value) => value._id === l._id);
    //       if (index < 0) {
    //         console.error('couldn\'t find the index of: ', l._id);
    //         return;
    //       }
    //       this._loglytics[index].membersRead = l.membersRead;
    //     });
    //   }); 
    // } catch (error) {
    //   console.log(error);
    // }
  }

  async getUnreadCount() {
    // try {
    //   const unreadCount = await loglyticsConnection.unreadCount();
    //   runInAction(() => {
    //     this.unreadCount = unreadCount;
    //   });
    // } catch (error) {
    //   console.log(error);
    // } 
  }

  // private fillMissingValues(dates: {date: Date, sessions: number}[]) {
  //   const currentDate = moment();
  //   let date: moment.Moment;
  //   date = moment().subtract(1, 'month').startOf('day');

  //   let newDates: {date: Date, sessions: number}[] = [];
  //   dates = dates.filter(d => date.isSameOrBefore(d.date));
  //   for (let i = 0; date.isBefore(currentDate) ; date = date.add(1, 'day')) {
  //     if (i < dates.length && date.isSame(dates[i].date, 'day')) {
  //       newDates.push(dates[i++]);
  //     }
  //     else {
  //       newDates.push({
  //         date: date.toDate(),
  //         sessions: 0,
  //       });
  //     }
  //   }
  //   return newDates;
  // }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  async setRange(range: RangeMode) {
    this.range = range;
    await this.getLoglytics();
    if (!this.currentLoglytics) return;
    await this.setKey(this.currentLoglytics.key, true);
    // Session-specific drill-ins (session + hasId) ignore the range buttons
    if (!searchStore.selectedOptions.some(o => o.type === SearchParam.KEY)) return;
    searchStore.setLoglyticsKey(this.currentLoglytics.key, range);
    await logsStore.getLogs(true);
  }
}
export default new LoglyticsStore();